import React, { useEffect, useState, useCallback } from "react";
import { 
  CreateTable,
  SuccessMessage,
  FailMessage,
  Loading
} from "../../common/APICommon";
import { apiUrl } from "../../utils/Configuration";
import NoAccess from "../NoAccess";
import axios from 'axios';
import { motion, AnimatePresence } from "framer-motion";
import EditIcon from '@mui/icons-material/Edit';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import WifiChannelIcon from '@mui/icons-material/WifiChannel';

export default function DistributionChannel() {
//#region Others
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [failMessage, setFailMessage] = useState("");
  const [filteredData, setFilteredData] = useState([]);
  const [search, setSearch] = useState("");

  const handleSearch = (value) => {
    setSearch(value);
    setCurrentPage(1)
    const lowerValue = value.toLowerCase();

    const filtered = filteredData.filter(
      (item) =>
        item.description.toLowerCase().includes(lowerValue) ||
        item.id.toString().includes(lowerValue)
    );

    setData(filtered);
  };
//#endregion




//#region Variables
  const [selectedData, setSelectedData] = useState(null);
  const [description, setDescription] = useState("");
  const [isActive, setIsActive] = useState(true);

  const resetVariables = () => {
    setDescription("");
    setIsActive(true);
    setSelectedData(null);
  }
//#endregion




// #region Add function
  const [showAddForm, setShowAddForm] = useState(false);
  
  const openAddForm = () => {
    setShowAddForm(true);
  }
  const closeAddForm = () => {
    setShowAddForm(false);
    resetVariables();
  }

  const handleAddData = async () => {
    const data = {
      description: description,
      isActive: isActive
    };

    try {
      setLoading(true);

      await axios.post(`${apiUrl}/api/DistributionChannel`, data, {
          headers: { 'Content-Type': 'application/json' }
      });
      
      await fetchData();
      setSuccessMessage("Data added successfully!");
    } catch (error) {
      console.error('There was a problem adding data:', error);
      const errorMessage = JSON.parse(error.response.request.responseText).message;
      setFailMessage(`Failed to add data: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  } 
//#endregion




// #region Update function
  const [showUpdateForm, setShowUpdateForm] = useState(false);
  
  const openUpdateForm = () => {
    setShowUpdateForm(true);
  }
  const closeUpdateForm = () => {
    setShowUpdateForm(false);
    resetVariables();
  }

  const editHandleClick = (data) => {
    setSelectedData(data);

    setDescription(data.description);
    setIsActive(data.isActive);

    openUpdateForm();
  }

  const handleUpdateData = async () => {
    const data = {
      description: description,
      isActive: isActive
    };

    try {
      setLoading(true);

      await axios.put(`${apiUrl}/api/DistributionChannel/${selectedData?.id}`, data, {
          headers: { 'Content-Type': 'application/json' }
      });
      
      await fetchData();
      setSuccessMessage("Data updated successfully!");
    } catch (error) {
      console.error('There was a problem adding data:', error);
      const errorMessage = JSON.parse(error.response.request.responseText).message;
      setFailMessage(`Failed to add data: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  } 
//#endregion




//#region Table data
  const [currentPage, setCurrentPage] = useState(1);
  const [data, setData] = useState([]);

  const headers = [
    'ID', 
    'DESCRIPTION', 
    'STATUS', 
    'ACTIONS',
  ]
  const accessors = [
    'id', 
    'description', 
    'isActive', 
    'actions'
  ]

  const actions = [
    { 
      name: "Edit", 
      tooltip: "edit",
      icon: <EditIcon fontSize="small" />,
      className: "bg-green-500 hover:bg-green-600",
      function: editHandleClick,
      visible: true,
    },
  ];

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);

      const response = await axios.get(`${apiUrl}/api/DistributionChannel` , {
        headers: { 'Content-Type': 'application/json'}
      })
        
      setData(response.data)
      setFilteredData(response.data)
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData])
//#endregion




//#region Access Template
  const accountData = JSON.parse(localStorage.getItem("accountData"));
  
  if (!accountData?.accessTemplate?.hasDashboard) {
    return <NoAccess />;
  }
//#endregion





//#region UI
  return (
    <>
      <div className="p-6 bg-gray-50 min-h-screen">
        {/* Header Section */}
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-4">
          <div>
            <h2 className="text-3xl font-bold text-gray-800 flex items-center gap-2">
              <WifiChannelIcon fontSize="large" className="text-theme-color" />
              {`Distribution Channel (${data?.length ?? 0})`}
            </h2>
            <p className="text-gray-500 mt-1">Way a company sells and delivers its products or services to customers.</p>
          </div>
          <div className="flex gap-3">
            <button onClick={openAddForm} className="flex items-center gap-2 bg-theme-color text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-theme-color-h transition shadow-md">
              <AddCircleOutlineIcon fontSize="small" /> New Channel
            </button>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="flex justify-between items-center gap-2">
          <div className="mb-4 w-full">
            <input
              type="text"
              placeholder="Search by description or ID..."
              className="px-4 py-2 border rounded-lg w-full md:w-2/3 focus:outline-none focus:ring-2 focus:ring-theme-color focus:border-theme-color"
              value={search}
              onChange={(e) => handleSearch(e.target.value)}
            />
            {/* Add filter dropdowns here if needed */}
          </div>
        </div>

        <CreateTable
          tableName={"Distribution Channel"}
          headers={headers}
          accessors={accessors}
          data={data}
          actions={actions}

          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          itemsPerPage={10}
          totalItems={data?.length}
        />
      </div>

      <AddUpdateModal 
        visible={showAddForm}
        onClose={closeAddForm}
        onSave={handleAddData}
        modalName={"Add Data"}

        data={data}
        description={description} setDescription={setDescription}
        isactive={isActive} setIsactive={setIsActive}
      />

      <AddUpdateModal 
        visible={showUpdateForm}
        onClose={closeUpdateForm}
        onSave={handleUpdateData}
        modalName={"Update Data"}

        data={data}
        description={description} setDescription={setDescription}
        isactive={isActive} setIsactive={setIsActive}
      />

      <div className="fixed top-5 right-5 z-50 flex flex-col space-y-2">
        <SuccessMessage message={successMessage} onClose={() => setSuccessMessage("")} />
        <FailMessage message={failMessage} onClose={() => setFailMessage("")} />
      </div>

      { loading && <Loading />}
    </>
  );
//#endregion
}







//#region AddUpdate Modal
const AddUpdateModal = ({
  visible, onClose, onSave, modalName,
  data,
  description, setDescription,
  isactive, setIsactive,
}) => {
  const [failMessage, setFailMessage] = useState("");

  if (!visible) return null;

  const handleSubmit = async () => {
    if (!description) {
      setFailMessage("Please fill up required field/s.");
      return;
    }
    await onSave();
    closeModal();
  };

  const resetLocalVariables = () => {
    setFailMessage("");
  };

  const closeModal = () => {
    onClose();
    resetLocalVariables();
  };

  return (
    <>
      {/* Background overlay */}
      <AnimatePresence>
        {visible && (
          <motion.div
            // initial={{ opacity: 0 }}
            // animate={{ opacity: 1 }}
            // exit={{ opacity: 0 }}
            className="fixed inset-0 bg-gray-200 bg-opacity-70 flex items-center justify-center z-50"
          >
            {/* Modal container */}
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="bg-gray-100 rounded-2xl shadow-lg w-full max-w-lg p-8"
            >
              <h2 className="text-2xl font-semibold border-b pb-3 mb-6">
                {modalName}
              </h2>

              <div className="space-y-5 max-h-[500px] overflow-y-auto short:max-h-[300px] medium:h-[400px] pr-1">
                <div>
                  <label className="block text-sm font-medium mb-1 text-gray-700 ">
                    Description <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. Direct Sales"
                    className="w-full border border-gray-300  rounded-lg px-3 py-2 text-gray-800 focus:outline-none focus:ring-2 focus:ring-theme-color focus:border-theme-color transition"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                  />
                </div>

                <div className="flex items-center justify-between border border-gray-300 rounded-lg p-3 bg-white">
                  <span className="text-sm font-medium text-gray-700">
                    Active Status
                  </span>
                  <button
                    type="button"
                    onClick={() => setIsactive(!isactive)}
                    className={`relative inline-flex h-7 w-14 items-center rounded-full transition-colors duration-300 focus:outline-none ${
                      isactive ? "bg-theme-color" : "bg-gray-400"
                    }`}
                  >
                    <span
                      className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-md transition-transform duration-300 ${
                        isactive ? "translate-x-7" : "translate-x-1"
                      }`}
                    />
                  </button>
                </div>
              </div>

              {/* Buttons */}
              <div className="flex justify-end space-x-3 mt-8 border-t pt-4">
                <button
                  onClick={closeModal}
                  className="px-5 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 transition"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  className="px-5 py-2 rounded-lg bg-theme-color text-white font-medium hover:opacity-90 transition"
                >
                  Save
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Alert */}
      <div className="fixed top-5 right-5 z-[9999] flex flex-col space-y-2">
        <FailMessage message={failMessage} onClose={() => setFailMessage("")} />
      </div>
    </>
  );
};
//#endregion